import { AfterViewInit, Component, ElementRef, OnDestroy, ViewChild, input, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BrowserQRCodeReader, IScannerControls } from '@zxing/browser';

import { TPipe } from '../../../pipe/t.pipe';
import { ModalComponent } from '../modal/modal.component';

/**
 * Scanner de QR (caméra) pour la remise et la restitution d'une location : le client présente
 * son code sur nawam.net, le loueur le scanne ici. Décodage `@zxing/browser` sur le flux vidéo
 * (caméra arrière si disponible) ; secours « coller le code » si la caméra est indisponible.
 * Émet `scanned` avec la chaîne brute (`NAWAM-RENTAL-START:…` / `NAWAM-RENTAL-END:…`).
 */
@Component({
  selector: 'app-qr-scanner',
  standalone: true,
  imports: [FormsModule, ModalComponent, TPipe],
  template: `
    <app-modal [isOpen]="true" (close)="closed.emit()" className="max-w-lg p-6">
      <h3 class="text-lg font-semibold text-gray-800 dark:text-white/90">{{ title() | t }}</h3>
      <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">{{ hint() | t }}</p>
      <div class="mt-4 overflow-hidden rounded-xl bg-black">
        <video #video class="aspect-square w-full object-cover" muted playsinline></video>
      </div>
      @if (cameraError()) {
        <p class="mt-3 rounded-lg bg-warning-50 px-3 py-2 text-sm text-warning-700 dark:bg-warning-500/10 dark:text-warning-400">{{ cameraError()! | t }}</p>
      }
      @if (busy()) {
        <p class="mt-3 text-sm text-gray-500 dark:text-gray-400">{{ 'rentals.scan.checking' | t }}</p>
      }
      @if (error()) {
        <p class="mt-3 rounded-lg bg-error-50 px-3 py-2 text-sm text-error-700 dark:bg-error-500/10 dark:text-error-400">{{ error() }}</p>
      }
      <details class="mt-4 text-sm">
        <summary class="cursor-pointer text-gray-600 dark:text-gray-300">{{ 'rentals.scan.manualTitle' | t }}</summary>
        <div class="mt-2 flex gap-2">
          <input [(ngModel)]="manual" type="text" [placeholder]="'rentals.scan.manualPlaceholder' | t" class="h-10 flex-1 rounded-lg border border-gray-300 bg-transparent px-3 text-sm text-gray-800 placeholder:text-gray-400 focus:border-brand-300 focus:outline-none dark:border-gray-700 dark:text-white/90" />
          <button type="button" (click)="submitManual()" [disabled]="!manual.trim() || busy()" class="inline-flex items-center rounded-lg bg-brand-500 px-4 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-50">{{ 'rentals.scan.manualSubmit' | t }}</button>
        </div>
      </details>
    </app-modal>
  `,
})
export class QrScannerComponent implements AfterViewInit, OnDestroy {
  readonly title = input('rentals.scan.title');
  readonly hint = input('rentals.scan.hint');
  /** Vérification en cours (le parent appelle le back) : on gèle le scan. */
  readonly busy = input(false);
  /** Erreur renvoyée par le parent (QR refusé, 409 photos manquantes…) : on relance le scan. */
  readonly error = input<string | null>(null);
  readonly scanned = output<string>();
  readonly closed = output<void>();

  @ViewChild('video') private video!: ElementRef<HTMLVideoElement>;
  protected readonly cameraError = signal<string | null>(null);
  protected manual = '';
  private controls: IScannerControls | null = null;
  private lastPayload = '';

  async ngAfterViewInit(): Promise<void> {
    try {
      const reader = new BrowserQRCodeReader();
      const devices = await BrowserQRCodeReader.listVideoInputDevices();
      const back = devices.find((d) => /back|rear|environment|arrière/i.test(d.label)) ?? devices[devices.length - 1];
      this.controls = await reader.decodeFromVideoDevice(back?.deviceId, this.video.nativeElement, (result) => {
        if (!result) return;
        const text = result.getText();
        // Un QR reste dans le champ plusieurs images de suite : n'émettre qu'une fois par code.
        if (text === this.lastPayload || this.busy()) return;
        this.lastPayload = text;
        this.scanned.emit(text);
      });
    } catch {
      this.cameraError.set('rentals.scan.cameraUnavailable');
    }
  }

  /** Permet de rescanner le même code après une erreur côté back. */
  reset(): void {
    this.lastPayload = '';
  }

  protected submitManual(): void {
    const v = this.manual.trim();
    if (v) { this.lastPayload = v; this.scanned.emit(v); }
  }

  ngOnDestroy(): void {
    try { this.controls?.stop(); } catch { /* flux déjà arrêté */ }
  }
}
